import {useEffect, useState} from "react";
import {v4 as uuidv4} from "uuid";
import { jwtDecode } from "jwt-decode";
import {useHandler} from "./useHandler";
import EventBus from "./EventBus";

type PickValue = {
    USID: string,
    Name: string,
    Group: string,
    Order: number,
    Type: string,
    Value: string
};

type Position = {
    USID: string,
    Name: string,
    OrganizationUSID: string
};

type UserGroup = {
    USID: string,
    Name: string
};

type WorkGroup = {
    USID: string,
    Name: string
};

type Role = {
    Name: string,
    USID: string
};

export type Visibility = {
    Mode: string,
    Value: string
}

export type Tenant = {
    USID: string,
    Name: string
}

export interface Permission {
    GroupUSID: string;
    GroupName: string;
    GroupType: string;
    Permissions: string[];
    Tags: string[];
}

export interface Company {
    Positions: [Position],
    UserGroups: [UserGroup],
    WorkGroups: [WorkGroup]
}

export class SessionContext {
    sessionUSID: string | null;
    identityToken: string | null;
    accessToken: string | null;
    accessTokenClaims: string | null;
    identityTokenClaims: string | null;
    sessionId: string;
    refreshToken: string | null;
    showLoginDialog: boolean;
    isLoggedIn: boolean;
    activeVisibility: Visibility | null;
    company: Company | null;
    pickValues: PickValue[];
    roles: Role[];
    tenants: Tenant[];
    privileges: string[];
    permissions: Permission[];
    generalPurposeVariable: string | null;
    S3AccessKeyId: string | null;
    S3SecretAccessKey: string | null;
    S3SessionToken: string | null;
    showGlobalMenu: boolean | false;

    name: string | null;
    email: string | null;
    companyName: string | null;
    companyUSID: string | null;
    employeeUSID: string | null;
    realm: string | null;
    userUSID: string | null;

    constructor() {
        this.sessionUSID = null;
        this.identityToken = null;
        this.accessToken = null;
        this.accessTokenClaims = null;
        this.companyName = null;
        this.companyUSID = null;
        this.employeeUSID = null;
        this.identityTokenClaims = null;
        this.refreshToken = null;
        this.name = null;
        this.email = null;
        this.realm = null;
        this.userUSID = null;
        this.showLoginDialog = true;
        this.isLoggedIn = false;
        this.activeVisibility = null;
        this.company = null;
        this.pickValues = []
        this.roles = [];
        this.privileges = [];
        this.permissions = [];
        this.tenants = [];
        this.generalPurposeVariable = null;
        this.S3AccessKeyId = null;
        this.S3SecretAccessKey = null;
        this.S3SessionToken = null;
        this.showGlobalMenu = false

        this.sessionId = SessionContext.loadSessionId();
    }

    static loadSessionId() {
        let sessionId: string | null = sessionStorage.getItem("sessionId");
        if (sessionId === null) {
            sessionId = uuidv4();
            sessionStorage.setItem("sessionId", sessionId);
        }
        return sessionId;
    }

    convert(type: any, value: any) {
        if (value === "null") value = null;
        if (value === null) return null;
        if (value === undefined) return undefined;
        if (typeof value === "string") {
            if (type === Boolean) return value === "true";
            if (type === Object) return JSON.parse(value);
        }
        return value;
    }

    getAccessToken(): string | null {
        this.accessToken = sessionStorage.getItem("AccessToken")

        return this.accessToken;
    }

    getAccessTokenClaims(): string | null {
        this.accessTokenClaims = sessionStorage.getItem("AccessTokenClaims")

        return this.accessTokenClaims;
    }

    getActiveVisibility(): Visibility | null {
        this.activeVisibility = JSON.parse(sessionStorage.getItem("ActiveVisibility")!)

        if (this.activeVisibility == null) {
            return {
                Mode: "",
                Value: ""
            };
        }
        return this.activeVisibility;
    }

    getCompany(): Company | null {
        this.company = this.convert(Object, sessionStorage.getItem("Company"))

        return this.company;
    }

    getCompanyName(): string | null {
        this.companyName = sessionStorage.getItem("CompanyName")

        return this.companyName;
    }

    getCompanyUSID(): string | null {
        this.companyUSID = sessionStorage.getItem("CompanyUSID")

        return this.companyUSID;
    }

    getEmployeeUSID(): string | null {
        this.employeeUSID = sessionStorage.getItem("EmployeeUSID")

        return this.employeeUSID;
    }

    getGeneralPurposeVariable(): string | null {
        this.generalPurposeVariable = sessionStorage.getItem("GeneralPurposeVariable")

        return this.generalPurposeVariable;
    }

    getIdentityToken(): string | null {
        this.identityToken = sessionStorage.getItem("IdentityToken")

        return this.identityToken;
    }

    getIdentityTokenClaims(): string | null {
        this.identityTokenClaims = sessionStorage.getItem("IdentityTokenClaims")

        return this.identityTokenClaims;
    }

    getName(): string {
        this.name = sessionStorage.getItem("Name")!

        return this.name;
    }

    getPermissions(): Permission[] {
        const permissionsData = sessionStorage.getItem("persist:auth");
        if (!permissionsData) return [];
        
        try {
            const parsed = JSON.parse(permissionsData);
            this.permissions = parsed.Permissions || [];
            return this.permissions;
        } catch (error) {
            console.error("Error reading permissions from auth:", error);
            return [];
        }
    }

    getPickValues(): PickValue[] {
        this.pickValues = JSON.parse(sessionStorage.getItem("PickValues")!)

        return this.pickValues;
    }

    getPrivileges(): string[] {
        this.privileges = JSON.parse(sessionStorage.getItem("Privileges")!)

        if (this.privileges === null) {
            return []
        }
        return this.privileges;
    }

    getRealm(): string | null {
        this.realm = sessionStorage.getItem("Realm")!

        return this.realm;
    }

    getRefreshToken(): string | null {
        this.refreshToken = sessionStorage.getItem("RefreshToken")

        return this.refreshToken;
    }

    getRoles(): Role[] {
        this.roles = JSON.parse(sessionStorage.getItem("Roles")!)

        return this.roles;
    }

    getS3AccessKeyId(): string | null {
        this.S3AccessKeyId = sessionStorage.getItem("S3AccessKeyId")

        return this.S3AccessKeyId;
    }

    getS3SecretAccessKey(): string | null {
        this.S3SecretAccessKey = sessionStorage.getItem("S3SecretAccessKey")

        return this.S3SecretAccessKey;
    }

    getS3SessionToken(): string | null {
        this.S3SessionToken = sessionStorage.getItem("S3SessionToken")

        return this.S3SessionToken;
    }

    getShowGlobalMenu(): boolean {
        this.showGlobalMenu = sessionStorage.getItem("ShowGlobalMenu") === 'true'

        return this.showGlobalMenu;
    }

    getShowLoginDialog(): boolean {
        this.showLoginDialog = sessionStorage.getItem("ShowLoginDialog") === 'true'

        return this.showLoginDialog;
    }

    getTenants(): Tenant[] {
        this.tenants = JSON.parse(sessionStorage.getItem("Tenants")!)

        return this.tenants;
    }

    jwtDecode(token: string | null): any {
        if (token === null) {
            return null;
        }
        try {
            return jwtDecode(token);
        } catch (e) {
            return null;
        }
    }

    setCompanyName(companyName: string) {
        this.companyName = companyName;
        sessionStorage.setItem("CompanyName", companyName)
        EventBus.session.emit("update", {"CompanyName": this.companyName});
    }

    setCompanyUSID(companyUSID: string) {
        this.companyUSID = companyUSID;
        sessionStorage.setItem("CompanyUSID", companyUSID)
        EventBus.session.emit("update", {"CompanyUSID": this.companyUSID});
    }

    setAccessToken(accessToken: string) {
        this.accessToken = accessToken;
        sessionStorage.setItem("AccessToken", accessToken)

        const claims: any = this.jwtDecode(accessToken);

        this.setAccessTokenClaims(claims);
        EventBus.session.emit("update", {"AccessToken": this.accessToken});
    }

    setAccessTokenClaims(claims: string) {
        this.accessTokenClaims = claims
        sessionStorage.setItem("AccessTokenClaims", this.accessTokenClaims)
        EventBus.session.emit("update", {"AccessTokenClaims": this.accessTokenClaims});
    }

    setActiveVisibility(visibilityMode: string, visibilityValue: string) {
        this.activeVisibility = {
            Mode: visibilityMode,
            Value: visibilityValue
        }
        sessionStorage.setItem("ActiveVisibility", JSON.stringify(this.activeVisibility))
        EventBus.session.emit("update", {"ActiveVisibility": this.activeVisibility});
    }

    setCompany(company: Company) {
        this.company = company
        sessionStorage.setItem("Company", JSON.stringify(company))
        EventBus.session.emit("update", {"Company": this.company});
    }

    setEmail(email: string) {
        this.email = email
        sessionStorage.setItem("EMail", this.email)
        EventBus.session.emit("update", {"EMail": this.email});
    }

    setEmployeeUSID(employeeUSID: string) {
        this.employeeUSID = employeeUSID
        sessionStorage.setItem("EmployeeUSID", this.employeeUSID)
        EventBus.session.emit("update", {"EmployeeUSID": this.employeeUSID});
    }

    setGeneralPurposeVariable(generalPurposeVariable: string) {
        this.generalPurposeVariable = generalPurposeVariable
        sessionStorage.setItem("GeneralPurposeVariable", this.generalPurposeVariable)
        EventBus.session.emit("update", {"GeneralPurposeVariable": this.generalPurposeVariable});
    }

    setIdentityTokenClaims(claims: string) {
        this.identityTokenClaims = claims
        sessionStorage.setItem("IdentityTokenClaims", this.identityTokenClaims)
        EventBus.session.emit("update", {"IdentityTokenClaims": this.identityTokenClaims});
    }

    setIdentityToken(identityToken: string) {
        this.identityToken = identityToken;
        sessionStorage.setItem("IdentityToken", identityToken)

        const claims: any = this.jwtDecode(identityToken);

        this.setIdentityTokenClaims(claims);
        this.setName(claims.name)
        this.setCompanyName(claims.companyName);
        this.setEmail(claims.email);
        this.setEmployeeUSID(claims.employeeUSID);
        this.setRealm(claims.realm);
        this.setUserUSID(claims.userUSID);
    }

    setName(name: string) {
        this.name = name
        sessionStorage.setItem("Name", this.name)
        EventBus.session.emit("update", {"Name": this.name});
    }

    setPermissions(permission: Permission[]) {
        // Store the entire auth response in sessionStorage
        sessionStorage.setItem("persist:auth", JSON.stringify({ Permissions: permission }));
        this.permissions = permission || [];
        EventBus.session.emit("update", {"Permissions": this.permissions});
    }

    setPickValues(pickValues: PickValue[]) {
        this.pickValues = pickValues
        sessionStorage.setItem("PickValues", JSON.stringify(pickValues))
        EventBus.session.emit("update", {"PickValues": this.pickValues});
    }

    setPrivileges(input: any) {
        let keys = Object.keys(input)

        this.privileges = [];

        for (let i = 0; i < keys.length; i++) {
            this.privileges.push(keys[i])
        }
        sessionStorage.setItem("Privileges", JSON.stringify(this.privileges))

        EventBus.session.emit("update", {"Privileges": this.privileges});
    }

    setRealm(realm: string) {
        this.realm = realm
        sessionStorage.setItem("Realm", this.realm)
        EventBus.session.emit("update", {"Realm": this.realm});
    }

    setRefreshToken(refreshToken: string) {
        this.refreshToken = refreshToken
        sessionStorage.setItem("RefreshToken", this.refreshToken)
        EventBus.session.emit("update", {"RefreshToken": this.refreshToken});
    }

    setRoles(roles: Role[]) {
        this.roles = roles
        sessionStorage.setItem("Roles", JSON.stringify(roles))

        EventBus.session.emit("update", {"Roles": this.roles});
    }

    setS3AccessKeyId(S3AccessKeyId: string) {
        this.S3AccessKeyId = S3AccessKeyId
        sessionStorage.setItem("S3AccessKeyId", S3AccessKeyId)
        EventBus.session.emit("update", {"S3AccessKeyId": this.S3AccessKeyId});
    }

    setS3SecretAccessKey(S3SecretAccessKey: string) {
        this.S3SecretAccessKey = S3SecretAccessKey
        sessionStorage.setItem("S3SecretAccessKey", S3SecretAccessKey)
        EventBus.session.emit("update", {"S3SecretAccessKey": this.S3SecretAccessKey});
    }

    setS3SessionToken(S3SessionToken: string) {
        this.S3SessionToken = S3SessionToken
        sessionStorage.setItem("S3SessionToken", S3SessionToken)
        EventBus.session.emit("update", {"S3SessionToken": this.S3SessionToken});
    }

    setSessionUSID(sessionUSID: string) {
        this.sessionUSID = sessionUSID
        sessionStorage.setItem("SessionUSID", sessionUSID)
        EventBus.session.emit("update", {"SessionUSID": this.sessionUSID});
    }

    setShowGlobalMenu(showGlobalMenu: boolean) {
        this.showGlobalMenu = showGlobalMenu
        sessionStorage.setItem("ShowGlobalMenu", showGlobalMenu.toString())
        EventBus.session.emit("update", {"ShowGlobalMenu": this.showGlobalMenu});
    }

    setShowLoginDialog(loginDialog: boolean) {
        this.showLoginDialog = loginDialog
        sessionStorage.setItem("ShowLoginDialog", loginDialog.toString())
        EventBus.session.emit("update", {"ShowLoginDialog": this.showLoginDialog});
    }

    setTenants(tenants: Tenant[]) {
        this.tenants = tenants
        sessionStorage.setItem("Tenants", JSON.stringify(tenants))

        EventBus.session.emit("update", {"Tenants": this.tenants});
    }

    setUserUSID(userUSID: string) {
        this.userUSID = userUSID
        sessionStorage.setItem("UserUSID", this.userUSID)
        EventBus.session.emit("update", {"UserUSID": this.userUSID});
    }

    valid() {
        return Boolean(this.getIdentityTokenClaims() && this.getAccessTokenClaims());
    }

}

export function hasPrivilege(requiredPrivilege: string) {

    let sessionContext = new SessionContext();

    let privileges = sessionContext.getPrivileges();
    for (let j = 0; j < privileges.length; j++) {
        if (requiredPrivilege === privileges[j]) {
            return true
        }
    }

    return false
}

export function getUserPermissionsFromAuth(): string[] {
    try {
        const authData = sessionStorage.getItem("persist:auth");

        console.log("Auth data from sessionStorage:", authData); // Debug log
        if (!authData) return [];

        const parsed = JSON.parse(authData);
        
        // Check if Permissions array exists (array of permission groups)
        if (!parsed.Permissions || !Array.isArray(parsed.Permissions)) {
            return [];
        }

        // Flatten all permissions from all groups
        const permissionsSet = new Set<string>();
        parsed.Permissions.forEach((group: Permission) => {
            if (group.Permissions && Array.isArray(group.Permissions)) {
                group.Permissions.forEach(perm => permissionsSet.add(perm));
            }
        });

        return Array.from(permissionsSet);
    } catch (error) {
        console.error("Error reading user permissions from auth:", error);
        return [];
    }
}

export function hasAnyUserPermission(requiredPermissions: string[]): boolean {
    const permissions = getUserPermissionsFromAuth();
    return requiredPermissions.some(perm => permissions.includes(perm));
}

export function hasCompanyCreatePermission(): boolean {
    if (process.env.REACT_APP_ENLIL_ENV === 'prod') {
        return false;
    }
    return hasAnyUserPermission(["COMPANY_CREATE", "COMPANY_BOOTSTRAP"]);
}


export function useSessionContext() {

    const [sessionContext, setSessionContext] = useState(new SessionContext());

    const updateListener = useHandler(
        () => setSessionContext(new SessionContext()),
        [sessionContext],
    );

    useEffect(() => {
        EventBus.session.addListener("update", updateListener);
        return () => {
            EventBus.session.removeListener("update", updateListener);
        }
    }, [updateListener]);

    return sessionContext;
}

